import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Magnetic from './ux/Magnetic';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    num: "01",
    title: "Discover",
    desc: "Deep-dive technical assessment, requirements gathering, and architecture planning to ensure alignment with business goals."
  },
  {
    num: "02",
    title: "Design & Build",
    desc: "Iterative development cycles using modern .NET and frontend frameworks, integrating AI models and Azure cloud infrastructure."
  },
  {
    num: "03",
    title: "Deploy & Support",
    desc: "Rigorous testing, seamless deployment pipelines, and ongoing enterprise support to ensure system longevity and performance."
  }
];

const Process = () => {
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const lineRef = useRef(null);
  const stepsRef = useRef([]);

  const pushStep = (el) => {
    if (el && !stepsRef.current.includes(el)) {
      stepsRef.current.push(el);
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title reveal
      gsap.fromTo(titleRef.current,
        { autoAlpha: 0, y: 30 },
        { autoAlpha: 1, y: 0, duration: 0.8, ease: "power3.out",
          scrollTrigger: { trigger: titleRef.current, start: "top 85%", once: true } }
      );

      // Line draw
      gsap.fromTo(lineRef.current,
        { scaleX: 0, transformOrigin: "left center" },
        { scaleX: 1, duration: 1.5, ease: "power3.inOut",
          scrollTrigger: { trigger: containerRef.current, start: "top 75%", once: true } }
      );

      // Steps stagger
      gsap.fromTo(stepsRef.current,
        { autoAlpha: 0, y: 40, scale: 0.9 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.8, stagger: 0.25, ease: "back.out(1.4)",
          scrollTrigger: { trigger: containerRef.current, start: "top 75%", once: true } }
      );
    });
    return () => ctx.revert();
  }, []);

  return (
    <section id="process" ref={containerRef} className="py-32 relative z-10 bg-white dark:bg-slate-950 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={titleRef} style={{ opacity: 0 }} className="text-center mb-24">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-slate-900 dark:text-white">How We Work</h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto text-lg">
            A proven methodology for delivering complex technical projects on time and to specification.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
          {/* Connecting Line - hidden on mobile */}
          <div ref={lineRef} className="hidden md:block absolute top-[48px] left-[16%] right-[16%] h-[2px] bg-slate-100 dark:bg-slate-800 z-0"></div>

          {steps.map((step, index) => (
            <div
              key={index}
              ref={pushStep}
              style={{ opacity: 0 }}
              className="relative z-10 flex flex-col items-center text-center"
            >
              <Magnetic strength={0.4}>
                <div className="w-24 h-24 rounded-full bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 flex items-center justify-center mb-8 shadow-md group hover:border-cyan-300 dark:hover:border-cyan-500 hover:shadow-cyan-500/20 transition-all duration-500 cursor-pointer" data-cursor="hover">
                  <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-br from-cyan-500 to-blue-600">
                    {step.num}
                  </span>
                </div>
              </Magnetic>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">{step.title}</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed max-w-xs">
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;